import {
  type ParentComponent,
  Show,
  createComputed,
  createContext,
  createEffect,
  createMemo,
  useContext,
} from 'solid-js'
import { RouteSkeleton } from '../shared/components/RouteSkeleton'
import { getPreferences, setLanguage as setStoredLanguage } from '../stores/preferences.store'
import { dictionaryLoadFailed, ensureDictionary, loadedDictionaries } from './locales'
import type { TranslationKey } from './locales/en'
import type { I18nContextValue, Locale } from './types'
import { LOCALE_INTL_TAGS, SUPPORTED_LOCALES } from './types'

const swallow = () => undefined

void ensureDictionary(getPreferences().language).catch(swallow)

if (getPreferences().language !== 'en') {
  const idle =
    'requestIdleCallback' in window
      ? window.requestIdleCallback
      : (cb: () => void) => window.setTimeout(cb, 2000)
  idle(() => void ensureDictionary('en').catch(swallow))
}

const I18nContext = createContext<I18nContextValue>()

const CatalogLoader = () => <RouteSkeleton />

export const I18nProvider: ParentComponent = (props) => {
  const locale = createMemo<Locale>(() => getPreferences().language)

  createComputed(() => {
    void ensureDictionary(locale()).catch(swallow)
  })

  createEffect(() => {
    const active = locale()
    if (loadedDictionaries()[active]) {
      document.documentElement.lang = LOCALE_INTL_TAGS[active]
    }
  })

  const setLocale = (newLocale: Locale) => {
    if (SUPPORTED_LOCALES.includes(newLocale)) {
      setStoredLanguage(newLocale)
    }
  }

  const t = (key: TranslationKey, params?: Record<string, string | number>): string => {
    const dicts = loadedDictionaries()
    let value = dicts[locale()]?.[key] ?? dicts.en?.[key] ?? key

    if (params) {
      for (const [paramKey, paramValue] of Object.entries(params)) {
        value = value.replace(new RegExp(`\\{\\{${paramKey}\\}\\}`, 'g'), () => String(paramValue))
      }
    }

    return value
  }

  const contextValue: I18nContextValue = {
    locale,
    setLocale,
    t,
    availableLocales: SUPPORTED_LOCALES,
  }

  let opened = false
  const ready = () => {
    if (opened) {
      return true
    }
    if (loadedDictionaries()[locale()] || dictionaryLoadFailed()) {
      opened = true
      return true
    }
    return false
  }

  return (
    <I18nContext.Provider value={contextValue}>
      <Show when={ready()} fallback={<CatalogLoader />}>
        {props.children}
      </Show>
    </I18nContext.Provider>
  )
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider')
  }
  return context
}
